import { parseISO } from "date-fns";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { getDateRangeQuery } from "components/DateRangeSelect/helpers";
import { EExamTableStatus, ERowColumn } from "components/VetExamsTable/types";
import DateRange, { DateRangeISO, DateRangeType } from "interfaces/DateRange";
import { SortOrder, SortOrderExtended } from "interfaces/SortOrder";
import { checkSortingType, reverseSortOrderValue } from "utils/sort-utils";
import {
  EPreRacesTableTab,
  EPreRaceTableSortingField,
  IPreRaceTableTab
} from "./types";

export interface IPreRacesTabsStatuses {
  isEntriesTab: boolean;
  isEntryExamsTab: boolean;
  isEntryResultsTab: boolean;
  isRaceDayTab: boolean;
  isRaceDayResultsTab: boolean;
}

export const checkCurrentRaceEntriesTableTab = (
  tab: EPreRacesTableTab
): IPreRacesTabsStatuses => {
  return {
    isEntriesTab: tab === EPreRacesTableTab.ENTRIES,
    isEntryExamsTab: tab === EPreRacesTableTab.ENTRY_EXAMS,
    isEntryResultsTab: tab === EPreRacesTableTab.ENTRY_RESULTS,
    isRaceDayTab: tab === EPreRacesTableTab.RACE_DAY,
    isRaceDayResultsTab: tab === EPreRacesTableTab.RACE_DAY_RESULTS
  };
};

export const createConfigTable = (
  selectedTab: IPreRaceTableTab
): ERowColumn[] => {
  switch (selectedTab.value) {
    case EPreRacesTableTab.ENTRY_EXAMS: {
      return [
        ERowColumn.HORSE_AND_TRAINER,
        ERowColumn.RACE_DATE,
        ERowColumn.RACE_NUMBER,
        ERowColumn.RACE_POSITION,
        ERowColumn.EXAM,
        ERowColumn.LAST_EXAM,
        ERowColumn.BARN,
        ERowColumn.STALL,
        ERowColumn.RACE_TYPE,
        ERowColumn.WATCH_LIST,
        ERowColumn.ASSIGNED_VET,
        ERowColumn.UNASSIGN,
        ERowColumn.RISK_LEVEL,
        ERowColumn.ACTIVE_VET_LISTS,
        ERowColumn.RISK_FACTOR_SCORE,
        ERowColumn.CSA,
        ERowColumn.HSF,
        ERowColumn.DLW,
        ERowColumn.DLR,
        ERowColumn.RISK_FACTORS,
        ERowColumn.EXTENDER
      ];
    }
    case EPreRacesTableTab.ENTRY_RESULTS: {
      return [
        ERowColumn.HORSE_AND_TRAINER,
        ERowColumn.RACE_DATE,
        ERowColumn.RACE_NUMBER,
        ERowColumn.RACE_POSITION,
        ERowColumn.RACE_TYPE,
        ERowColumn.ENTRY_REVIEW,
        ERowColumn.RISK_LEVEL,
        ERowColumn.BARN,
        ERowColumn.STALL,
        ERowColumn.CSA,
        ERowColumn.HSF,
        ERowColumn.DLW,
        ERowColumn.DLR
      ];
    }
    case EPreRacesTableTab.RACE_DAY: {
      return [
        ERowColumn.HORSE_AND_TRAINER,
        ERowColumn.RACE_DATE,
        ERowColumn.RACE_NUMBER,
        ERowColumn.RACE_POSITION,
        ERowColumn.ENTRY_REVIEW,
        ERowColumn.EXAM,
        ERowColumn.LAST_EXAM,
        ERowColumn.BARN,
        ERowColumn.STALL,
        ERowColumn.RACE_TYPE,
        ERowColumn.WATCH_LIST,
        ERowColumn.ASSIGNED_VET,
        ERowColumn.UNASSIGN,
        ERowColumn.RISK_LEVEL,
        ERowColumn.ACTIVE_VET_LISTS,
        ERowColumn.RISK_FACTOR_SCORE,
        ERowColumn.CSA,
        ERowColumn.HSF,
        ERowColumn.DLW,
        ERowColumn.DLR,
        ERowColumn.RISK_FACTORS,
        ERowColumn.EXTENDER
      ];
    }
    case EPreRacesTableTab.RACE_DAY_RESULTS: {
      return [
        ERowColumn.HORSE_AND_TRAINER,
        ERowColumn.RACE_DATE,
        ERowColumn.RACE_NUMBER,
        ERowColumn.RACE_POSITION,
        ERowColumn.RACE_TYPE,
        ERowColumn.RACE_DAY_REVIEW,
        ERowColumn.RISK_LEVEL,
        ERowColumn.BARN,
        ERowColumn.STALL,
        ERowColumn.CSA,
        ERowColumn.HSF,
        ERowColumn.DLW,
        ERowColumn.DLR,
        ERowColumn.ACTIVE_VET_LISTS,
        ERowColumn.RISK_FACTOR_SCORE,
        ERowColumn.RISK_FACTORS
      ];
    }
    default: {
      return [
        ERowColumn.HORSE_AND_TRAINER,
        ERowColumn.RACE_DATE,
        ERowColumn.RACE_NUMBER,
        ERowColumn.RACE_POSITION,
        ERowColumn.RACE_TYPE,
        ERowColumn.LAST_EXAM,
        ERowColumn.RISK_LEVEL,
        ERowColumn.WATCH_LIST,
        ERowColumn.BARN,
        ERowColumn.STALL,
        ERowColumn.CSA,
        ERowColumn.HSF,
        ERowColumn.DLW,
        ERowColumn.DLR,
        ERowColumn.ACTIVE_VET_LISTS,
        ERowColumn.RISK_FACTOR_SCORE,
        ERowColumn.RISK_FACTORS,
        ERowColumn.EXTENDER
      ];
    }
  }
};

export const getPreRaceExamTableStatus = (
  examType: racehorse360.HorseExamType,
  examResult: racehorse360.HorseExamResult
): EExamTableStatus => {
  if (examResult === racehorse360.HorseExamResult.HORSE_EXAM_RESULT_FAILED) {
    return EExamTableStatus.EXAM_FAILED;
  }

  if (examResult === racehorse360.HorseExamResult.HORSE_EXAM_RESULT_PASSED) {
    if (examType === racehorse360.HorseExamType.HORSE_EXAM_TYPE_QUICK_PASS) {
      return EExamTableStatus.QUICK_PASSED;
    }

    return EExamTableStatus.EXAM_PASSED;
  }
};

export const getConvertedToDateSelectedDateRange = (
  dateRangeISO: DateRangeISO
): DateRange => {
  if (!dateRangeISO) return { Type: DateRangeType.UpcomingDates };

  const dateRange: DateRange = {
    Type: dateRangeISO.Type
  };

  if (dateRangeISO.Type === DateRangeType.Custom) {
    dateRange.CustomDateRange = dateRangeISO.CustomDateRange.map(s =>
      parseISO(s)
    );
  }

  return dateRange;
};

export const getDateRangeFilter = (
  isRaceDayTab: boolean,
  dateRangeISO: DateRangeISO
): racehorse360.IDateMatch => {
  const convertedToDateSelectedDateRange =
    getConvertedToDateSelectedDateRange(dateRangeISO);
  const defaultDateRange = getDateRangeQuery(convertedToDateSelectedDateRange);

  if (isRaceDayTab) {
    return {
      range: {
        relative: {
          startDate: {
            value: 0
          },
          endDate: {
            value: 1
          }
        }
      }
    };
  }

  return defaultDateRange;
};

export const getSortingRules = (
  orderByOptions: EPreRaceTableSortingField[],
  sortOrder: SortOrder
): string[] => {
  const commonSortings = [
    `${EPreRaceTableSortingField.HORSE_NAME} ${SortOrder.ASC}`,
    `${EPreRaceTableSortingField.RACE_DATE} ${SortOrder.ASC}`
  ] as const;

  const inversedSortings = [
    EPreRaceTableSortingField.LAST_RACE_DATE,
    EPreRaceTableSortingField.LAST_WORKOUT_DATE
  ] as const;

  let actualSortOrder: SortOrder = sortOrder;

  if (inversedSortings.some(sortType => orderByOptions.includes(sortType))) {
    actualSortOrder = reverseSortOrderValue(sortOrder);
  }

  const { isOneOfAsc } = checkSortingType(actualSortOrder);
  const isSortingNullLast = [
    EPreRaceTableSortingField.RISK_LEVEL,
    EPreRaceTableSortingField.BARN_NAME
  ].some(sortType => orderByOptions.includes(sortType));
  const isDefaultAccordionView = [
    orderByOptions.length === 1,
    orderByOptions.includes(EPreRaceTableSortingField.TRAINER_LAST_NAME)
  ].every(Boolean);

  let columnSortings: string[] = [];

  if (isSortingNullLast) {
    const sortOrderWithNulls = isOneOfAsc
      ? SortOrderExtended.ASC_NULLS_LAST
      : SortOrderExtended.DESC_NULLS_LAST;
    columnSortings = orderByOptions.map(opt => `${opt} ${sortOrderWithNulls}`);
  } else if (isDefaultAccordionView) {
    columnSortings = orderByOptions.map(opt => {
      return `${opt} ${
        isOneOfAsc
          ? SortOrderExtended.ASC_NULLS_FIRST
          : SortOrderExtended.DESC_NULLS_FIRST
      }`;
    });
  } else {
    columnSortings = orderByOptions.map(opt => `${opt} ${actualSortOrder}`);
  }

  let specificColumnSortings: string[] = [];
  if (orderByOptions.includes(EPreRaceTableSortingField.IS_FLAGGED)) {
    specificColumnSortings = [
      `${EPreRaceTableSortingField.FLAG_PRIORITY} ${actualSortOrder}`
    ];
  } else if (orderByOptions.includes(EPreRaceTableSortingField.RACE_NUMBER)) {
    specificColumnSortings = [
      `${EPreRaceTableSortingField.RACE_POSITION} ${SortOrder.ASC}`
    ];
  } else if (orderByOptions.includes(EPreRaceTableSortingField.BARN_NAME)) {
    specificColumnSortings = [
      `${EPreRaceTableSortingField.TRAINER_LAST_NAME} ${SortOrderExtended.ASC_NULLS_LAST}`
    ];
  } else if (isDefaultAccordionView) {
    specificColumnSortings = [
      `${EPreRaceTableSortingField.IS_FLAGGED} ${SortOrder.DESC}`,
      `${EPreRaceTableSortingField.FLAG_PRIORITY} ${SortOrder.DESC}`,
      `${EPreRaceTableSortingField.RISK_LEVEL} ${SortOrderExtended.DESC_NULLS_LAST}`
    ];
  }

  return [...columnSortings, ...specificColumnSortings, ...commonSortings];
};
